import cn from "classnames";
import {
    BidControlPoints,
    BidDataFilter,
    BidDataLegend,
    BidItemsChanges,
    BidRoadRestrictions,
    CarStopPoints,
    DistributionBidRoute,
    EmergencyAreaPoints,
    EmulatedSegments,
    ExternalBidInfo,
    ItemChangePoints,
    MarkerCar,
    MarkerCluster,
    PaymentPoints,
    PlannedMarkerCar,
    PlatonPoints,
    PointPrediction,
    PointsFiltersMap,
    Postamats,
    RefuelingPoints,
    RegionDryingPeriods,
    RoadRestrictions,
    RoadRestrictionsList,
    RouteControlPoints,
    RouteView,
    SupportPoints,
    TrackerRoute,
    UnstableZones,
} from "components/map/shared";
import { getFirstPoint, getLastPoint } from "helpers/bidpoint";
import { featureModuleTypesEnum, routeTypesEnum } from "helpers/enums";
import { carStopMarkerStyle } from "helpers/map";
import { isRegionDryingPeriodsEnabledForClients } from "helpers/regionDryingPeriods";
import { canManageOrganization, formatNumber } from "helpers/system";
import memoize from "lodash/memoize";
import React from "react";
import { Polygon, Tooltip } from "react-leaflet";
import { useMapEvent } from "react-leaflet/hooks";
import { alert } from "services/alert";
import { getListForBid } from "sources/admin/registryPoints";

const areEqual = (prevProps, nextProps) => {
    return prevProps.carsLoadedAt === nextProps.carsLoadedAt;
};

const MapCarMarkers = React.memo((props) => {
    const { items, history, enums, organization } = props;
    return (
        <MarkerCluster>
            {items.map((item, i) => (
                <MarkerCar
                    withActions
                    key={i}
                    item={item}
                    history={history}
                    enums={enums}
                    organization={organization}
                />
            ))}
        </MarkerCluster>
    );
}, areEqual);

export default class MainMap extends PointsFiltersMap {
    constructor(props) {
        super(props);

        this.carCoords = memoize(this.carCoords);
    }

    carCoords = (carRoutes) => {
        const routePoints = [];
        const carCoords = (carRoutes || []).map((x) => {
            routePoints.push({
                isMobile: x.isMobile,
                hasActiveMileage: x.hasActiveMileage,
                points: x.traveledRouteFixedAtParts,
            });
            return {
                isMobile: x.isMobile,
                hasActiveMileage: x.hasActiveMileage,
                points: x.traveledRouteCoordinates || { [routeTypesEnum.EmptyMileage]: [], [routeTypesEnum.Main]: [] },
                emulatedSegments: x.emulatedSegments,
            };
        });
        return [carCoords, routePoints];
    };

    recalcMapPosition = (prevProps) => {
        if (!prevProps) {
            return null;
        }
        const { currentBid, route, items, carsLoadedAt, trackerCoords, trackerCoordsLoadedAt, bidPoints } =
            this.props.data;
        const points = [];
        const addCoordinates = (geozone) => {
            const coordinates = geozone.location.coordinates;
            points.push([coordinates[1], coordinates[0]]);
        };
        if (currentBid) {
            if ((currentBid !== prevProps.data.currentBid || (bidPoints && !prevProps.data.bidPoints)) && !route) {
                const { bidPoints } = this.props.data;
                if (bidPoints) {
                    const firstPoint = getFirstPoint(bidPoints);
                    const lastPoint = getLastPoint(bidPoints);

                    if (firstPoint) {
                        addCoordinates(firstPoint.geozone);
                    }
                    if (lastPoint) {
                        addCoordinates(lastPoint.geozone);
                    }
                }
            }
        } else if (
            currentBid !== prevProps.data.currentBid ||
            (items.length > 0 && carsLoadedAt && carsLoadedAt !== prevProps.data.carsLoadedAt)
        ) {
            items.map(addCoordinates);
        } else if (
            trackerCoords &&
            trackerCoords.length &&
            trackerCoordsLoadedAt &&
            trackerCoordsLoadedAt !== prevProps.data.trackerCoordsLoadedAt
        ) {
            points.push(trackerCoords);
        }
        if (points.length === 0) {
            return;
        }
        this.changeMapPosition(points);
    };

    renderCarMarkers = () => {
        const { history, enums, organization } = this.props;
        const { items, carsLoadedAt } = this.props.data;
        if (this.props.data.renderCarMarkers) {
            return (
                <MapCarMarkers
                    items={items || []}
                    carsLoadedAt={carsLoadedAt}
                    history={history}
                    enums={enums}
                    organization={organization}
                />
            );
        }
    };

    renderBidCarLocation = () => {
        const { bidLastCarLocation } = this.props.data;

        if (bidLastCarLocation) {
            const { estimatedCarLocation, estimatedAt } = bidLastCarLocation || {};
            return (
                <>
                    {!!bidLastCarLocation && <MarkerCar item={bidLastCarLocation} enums={this.props.enums} />}
                    {!!estimatedCarLocation && (
                        <PlannedMarkerCar item={estimatedCarLocation} estimatedAt={estimatedAt} />
                    )}
                </>
            );
        }
        return null;
    };

    renderDistributionBidRoute = () => {
        const { distributionBidRoute } = this.props.data;
        if (!distributionBidRoute) {
            return null;
        }
        return (
            <DistributionBidRoute
                distributionBidRoute={distributionBidRoute}
                changeMapPosition={this.changeMapPosition}
                showTollRoads={this.state.bidFilters.tollRoads}
            />
        );
    };

    renderRoute = () => {
        const { currentBid, route, carRoutes, bidPoints, expandedRangeTime, updateCarRoutes } = this.props.data;
        if (currentBid && route) {
            let carCoords = [];
            let routePoints = {};
            if (carRoutes) {
                [carCoords, routePoints] = this.carCoords(carRoutes);
            }
            const { zoom } = this.props.viewport;
            return (
                <RouteView
                    changeMapPosition={this.changeMapPosition}
                    route={route}
                    roles={this.props.roles}
                    expandedRangeTime={expandedRangeTime}
                    traveledRoute={carCoords}
                    routePoints={routePoints}
                    bidId={currentBid}
                    bidPoints={bidPoints}
                    organizationTimezone={this.props.organizationTimezone}
                    filters={this.state.bidFilters}
                    zoom={zoom}
                    selectGeozoneMode={this.state.selectGeozoneMode}
                    setGeozonePoint={this.setGeozonePoint}
                    mergeMapData={this.props.mergeMapData}
                    updateCarRoutes={updateCarRoutes}
                />
            );
        }
        return null;
    };

    renderRoutePoints = () => {
        const { bidPoints } = this.props.data;
        if (bidPoints && bidPoints.length) {
            getFirstPoint(bidPoints).first = getLastPoint(bidPoints).last = true;
            return this.renderRouteMarkers(bidPoints);
        }
        return null;
    };

    renderStopPoints = () => {
        const { carStops } = this.props.data;
        if (!carStops) {
            return null;
        }
        const { stops } = this.state.bidFilters;
        if (!stops) {
            return null;
        }

        const {
            viewport: { zoom },
        } = this.props;
        if (zoom < 8) {
            return null;
        }
        return <CarStopPoints carStops={carStops} markerStyle={carStopMarkerStyle} />;
    };

    renderRefuelingPoints = () => {
        const { refueling, refuelingServiceType } = this.props.data;
        if (!refueling) {
            return null;
        }
        const { refuelings } = this.state.bidFilters;
        if (!refuelings) {
            return null;
        }
        const {
            viewport: { zoom },
        } = this.props;
        if (zoom < 2) {
            return null;
        }
        return (
            <RefuelingPoints
                refueling={refueling}
                refuelingServiceType={refuelingServiceType}
                organization={this.props.organization}
            />
        );
    };

    renderSupportPoints = () => {
        const { supportPoints, supportRoute } = this.props.data;
        if (!supportPoints) {
            return null;
        }
        return <SupportPoints supportPoints={supportPoints} supportRoute={supportRoute} />;
    };

    renderBidControlPoints = () => {
        const { profile } = this.props;
        if (!profile.availableFeatureModules.includes(featureModuleTypesEnum.DriverControlPoints)) {
            return null;
        }
        const { currentBid, route } = this.props.data;
        if (!currentBid || !route) {
            return null;
        }
        return <BidControlPoints bidId={currentBid} />;
    };

    renderTrackerRoute = () => {
        const { trackerCoords, trackerStops } = this.props.data;
        if (trackerCoords) {
            return <TrackerRoute trackerCoords={trackerCoords || []} trackerStops={trackerStops || {}} />;
        }
        return null;
    };

    renderEmulatedRoute = () => {
        const { emulatedSegments } = this.props.data;
        if (emulatedSegments) {
            return <EmulatedSegments emulatedSegments={emulatedSegments || []} />;
        }
        return null;
    };

    renderBidDataFilter = () => {
        const { availableFeatureModules } = this.props.profile;
        const { currentBid } = this.props.data;
        if (!currentBid) {
            return null;
        }
        const { bidFilters } = this.state;
        return (
            <BidDataFilter
                roles={this.props.roles}
                filters={bidFilters}
                toggleGroupFilter={this.toggleGroupFilter}
                toggleFilter={this.toggleFilter}
                availableFeatureModules={availableFeatureModules}
                roadRestrictionTypes={this.props.roadRestrictionTypes}
            />
        );
    };

    roadRestrictionList = () => {
        const { currentBid, route, routeControlPoints } = this.props.data || {};
        if (!currentBid) {
            return null;
        }
        return <RoadRestrictionsList route={route} routeControlPoints={routeControlPoints} />;
    };

    formatRatePerKm = (rate) => {
        const { organization } = this.props;

        return `${formatNumber(rate, { style: "currency", currency: organization?.currency })}/км`;
    };

    getAverageRateMapInfo = () => {
        const { bidAverageRateInfo } = this.props.data;

        if (bidAverageRateInfo?.bidAverageRate == null) {
            return null;
        }

        const { averageRateByDestination, bidAverageRate } = bidAverageRateInfo;
        const hasMarketRate = averageRateByDestination != null && averageRateByDestination > 0;

        if (!hasMarketRate) {
            return {
                bidRate: this.formatRatePerKm(bidAverageRate),
                statusText: "Нет данных по рынку",
                statusClassName: "text-muted",
            };
        }

        const deviation = ((bidAverageRate - averageRateByDestination) / averageRateByDestination) * 100;

        if (!Number.isFinite(deviation)) {
            return {
                bidRate: this.formatRatePerKm(bidAverageRate),
                marketRate: this.formatRatePerKm(averageRateByDestination),
                statusText: "Нет данных по рынку",
                statusClassName: "text-muted",
            };
        }

        const roundedDeviationValue = Math.round(deviation * 10) / 10 || 0;
        const formattedDeviation = formatNumber(Math.abs(roundedDeviationValue), { maximumFractionDigits: 1 });
        const isInMarket = roundedDeviationValue === 0;
        const isHigher = roundedDeviationValue > 0;

        const statusText = isInMarket ? "В рынке" : `${isHigher ? "Выше" : "Ниже"} рынка на ${formattedDeviation}%`;
        const statusClassName = isInMarket ? "text-muted" : isHigher ? "text-success" : "text-danger";
        const marketRate = this.formatRatePerKm(averageRateByDestination);
        const bidRate = this.formatRatePerKm(bidAverageRate);

        return {
            bidRate,
            marketRate,
            statusText,
            statusClassName,
        };
    };

    renderAverageRateBar = (averageRateMapInfo) => (
        <div className="map__average-rate map__average-rate-bar" data-onboarding="bid-average-rate">
            {averageRateMapInfo.marketRate && (
                <>
                    <span>Рынок: {averageRateMapInfo.marketRate}</span>
                    <span className="map__average-rate-separator">|</span>
                </>
            )}
            <span>Заявка: {averageRateMapInfo.bidRate}</span>
            <span className="map__average-rate-separator">|</span>
            <span className={cn("map__average-rate-status", averageRateMapInfo.statusClassName)}>
                {averageRateMapInfo.statusText}
            </span>
        </div>
    );

    renderAverageRateInfo = () => {
        const averageRateMapInfo = this.getAverageRateMapInfo();

        if (!averageRateMapInfo) {
            return null;
        }

        return this.renderAverageRateBar(averageRateMapInfo);
    };

    renderExternalBidInfo = () => {
        const { currentBid, externalBidOutput, hasExternalBid, carId } = this.props.data;
        if (!currentBid || !hasExternalBid) {
            return null;
        }

        return (
            <ExternalBidInfo
                externalBidOutput={externalBidOutput}
                bidId={currentBid}
                carId={carId}
                modal={this.props.modal}
                organization={this.props.organization}
            />
        );
    };

    renderBidDataLegend = () => {
        const { currentBid } = this.props.data;
        if (!currentBid) {
            return null;
        }
        const { availableFeatureModules } = this.props.profile;
        return <BidDataLegend availableFeatureModules={availableFeatureModules} roles={this.props.roles} />;
    };

    renderBidItemsChanges = () => {
        const { currentBid, bidItemsChanges } = this.props.data;
        if (!(currentBid && bidItemsChanges)) {
            return null;
        }
        return <BidItemsChanges bidItemsChanges={bidItemsChanges} />;
    };

    renderPostamats = () => {
        const { currentBid } = this.props.data;
        const { postamatPoints } = this.props;
        if (!(currentBid && postamatPoints && this.state.bidFilters.postamats)) {
            return null;
        }
        return <Postamats postamats={postamatPoints} />;
    };

    renderUnstableZones = () => {
        const { currentBid, unstableZones } = this.props.data;
        if (
            !(
                currentBid &&
                unstableZones &&
                this.state.bidFilters.unstableZones &&
                canManageOrganization(this.props.roles)
            )
        ) {
            return null;
        }
        return <UnstableZones unstableZones={unstableZones} />;
    };

    renderRegionDryingPeriods = () => {
        if (!isRegionDryingPeriodsEnabledForClients) {
            return null;
        }

        const { regionDryingPeriods } = this.props;
        const { currentBid, regionDryingPeriodIds } = this.props.data;
        if (
            (!this.state.showRegionDryingFilters && !currentBid) ||
            (currentBid && !this.state.bidFilters.regionDryingFilters)
        ) {
            return null;
        }

        return (
            <RegionDryingPeriods
                regionDryingPeriods={regionDryingPeriods}
                regionDryingPeriodIds={regionDryingPeriodIds}
                currentBid={currentBid}
            />
        );
    };

    renderPlatonPoints = () => {
        const {
            data: { currentBid },
            platonPoints,
        } = this.props;
        if (!(currentBid && platonPoints && this.state.bidFilters.platonPoints)) {
            return null;
        }
        return <PlatonPoints platonPoints={platonPoints} />;
    };

    renderPaymentPoints = () => {
        const {
            data: { currentBid },
            paymentPoints,
            viewport,
        } = this.props;
        if (!(currentBid && paymentPoints && this.state.bidFilters.paymentPoints)) {
            return null;
        }
        return <PaymentPoints zoom={viewport.zoom} points={paymentPoints} />;
    };

    renderRoadRestrictions = () => {
        const { roadRestrictions, viewport } = this.props;
        return <RoadRestrictions zoom={viewport.zoom} points={roadRestrictions} filters={this.state.bidFilters} />;
    };

    renderBidRoadRestrictions = () => {
        const { currentBid, route } = this.props.data;
        if (!currentBid) {
            return null;
        }
        return <BidRoadRestrictions route={route} filters={this.state.bidFilters} />;
    };

    renderEmergencyAreaPoints = () => {
        const { currentBid, emergencyAreaPoints } = this.props.data;
        if (!(currentBid && emergencyAreaPoints && this.state.bidFilters.emergencyAreaPoints)) {
            return null;
        }
        return <EmergencyAreaPoints emergencyAreaPoints={emergencyAreaPoints} />;
    };

    renderRouteControlPoints = () => {
        const { currentBid, routeControlPoints } = this.props.data;
        const { availableFeatureModules } = this.props.profile;
        if (
            !(
                currentBid &&
                routeControlPoints &&
                this.state.bidFilters.routeControlPoints &&
                availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints)
            )
        ) {
            return null;
        }
        return <RouteControlPoints routeControlPoints={routeControlPoints} />;
    };

    renderItemChangePoints = () => {
        const { currentBid, itemChangePoints, updateItemChangePoint, canDragItemChangePoints } = this.props.data;
        if (!(currentBid && itemChangePoints)) {
            return null;
        }
        return (
            <ItemChangePoints
                itemChangePoints={itemChangePoints}
                data={this.props.data}
                modal={this.props.modal}
                mergeMapData={this.props.mergeMapData}
                updateItemChangePoint={updateItemChangePoint}
                canDragItemChangePoints={canDragItemChangePoints}
                organization={this.props.organization}
            />
        );
    };

    renderPointPrediction = () => {
        const { pointPrediction, updatePoint, currentBid } = this.props.data;
        if (!pointPrediction || !pointPrediction.addressInfo?.location?.coordinates) {
            return null;
        }
        return (
            <PointPrediction
                {...pointPrediction}
                changeMapPosition={this.changeMapPosition}
                updatePoint={updatePoint}
                currentBid={currentBid}
                mergeMapData={this.props.mergeMapData}
            />
        );
    };

    getRegistryPoints = (lon, lat) => {
        getListForBid(lon, lat).then((registryPoints) => {
            if (!this.state.checkRegistryPointsMode) {
                return;
            }
            this.setState({ registryPoints });
            if (registryPoints?.length === 0) {
                alert({ message: "Не найдены точки из реестра точек выгрузок", type: "warning" });
            }
        });
    };

    renderRegistryPoints = () => {
        const { registryPoints } = this.state;
        if (!registryPoints || registryPoints.length === 0) {
            return null;
        }
        return registryPoints.map((registryPoint, i) => {
            const { coordinates: c } = registryPoint;
            const points = [
                [c[0], c[2]],
                [c[1], c[2]],
                [c[1], c[3]],
                [c[0], c[3]],
            ];
            return (
                <Polygon
                    key={i}
                    positions={points}
                    color="#ffb200"
                    fill
                    fillColor="#ffb200"
                    fillOpacity={0.2}
                    weight={2}
                >
                    <Tooltip permanent direction="top">
                        {registryPoint.geohash}
                    </Tooltip>
                </Polygon>
            );
        });
    };

    renderBody = () => {
        const { selectGeozoneMode, checkRegistryPointsMode } = this.state;
        const { currentBid } = this.props.data;

        if (selectGeozoneMode) {
            return this.renderRoute();
        }

        if (checkRegistryPointsMode) {
            return (
                <>
                    {this.renderRoute()}
                    {currentBid && checkRegistryPointsMode && <MapClickHandler onClick={this.getRegistryPoints} />}
                    <div className="map-form">
                        <div className="map-form__head">
                            <div className="flex-1">Нажмите на карту, для проверки выбранной точки</div>
                            <div
                                className="map-form__head--btn"
                                onClick={() => this.setState({ checkRegistryPointsMode: false })}
                            >
                                <div className="map-form__head--icon icon-uEA07-delite" />
                            </div>
                        </div>
                    </div>
                    {this.renderRegistryPoints()}
                </>
            );
        }
        return (
            <>
                {this.renderCarMarkers()}
                {this.renderBidCarLocation()}
                {this.renderRoutePoints()}
                {this.renderRoute()}
                {this.renderDistributionBidRoute()}
                {this.renderStopPoints()}
                {this.renderTrackerRoute()}
                {this.renderEmulatedRoute()}
                {this.renderRefuelingPoints()}
                {this.renderSupportPoints()}
                {this.renderBidControlPoints()}
                {this.renderItemChangePoints()}
                {this.renderBidItemsChanges()}
                {this.renderPointPrediction()}
                {this.renderPostamats()}
                {this.renderUnstableZones()}
                {this.renderPlatonPoints()}
                {this.renderPaymentPoints()}
                {this.renderEmergencyAreaPoints()}
                {this.renderRouteControlPoints()}
                {this.renderRoadRestrictions()}
                {this.renderBidRoadRestrictions()}
                {this.renderRegionDryingPeriods()}
            </>
        );
    };
}

const MapClickHandler = (props) => {
    useMapEvent("click", ({ latlng }) => props.onClick(latlng.lng, latlng.lat));
    return null;
};
